import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { quantityStep, quantityMin, normalizeQuantity, unitLabel } from "@/lib/units";
import { resolveImageUrl } from "@/lib/api";

export default function CartPage() {
  const cart = useCart();
  const navigate = useNavigate();

  return (
    <div data-testid="cart-page">
      <Header />
      <section className="ff-container py-12">
        <p className="ff-overline">Votre commande</p>
        <h1 className="font-serif text-4xl lg:text-5xl mt-3">Panier</h1>

        {cart.items.length === 0 ? (
          <div className="mt-16 text-center" data-testid="empty-cart">
            <ShoppingBag className="w-12 h-12 text-[#8A9A8E] mx-auto" />
            <p className="mt-4 text-[#4A5D4E]">Votre panier est vide.</p>
            <Link to="/shop" data-testid="cart-empty-shop-link" className="ff-btn-primary mt-6 inline-flex">
              Découvrir le catalogue
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-3">
              {cart.items.map((i) => (
                <div key={i.product_id} className="ff-card p-4 flex items-center gap-4" data-testid={`cart-item-${i.product_id}`}>
                  <img src={resolveImageUrl(i.image_url)} alt={i.name} className="w-20 h-20 object-cover bg-[#F2EFEB]" />
                  <div className="flex-1">
                    <h3 className="font-serif text-lg">{i.name}</h3>
                    <p className="text-sm text-[#8A9A8E]">CHF {i.price.toFixed(2)} / {unitLabel(i.unit)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={quantityMin(i.unit)}
                      step={quantityStep(i.unit)}
                      value={i.quantity}
                      onChange={(e) => cart.setQuantity(i.product_id, e.target.value)}
                      onBlur={(e) => cart.setQuantity(i.product_id, normalizeQuantity(i.unit, e.target.value))}
                      className="ff-input w-20 text-center"
                      data-testid={`cart-qty-${i.product_id}`}
                    />
                    <span className="text-xs text-[#8A9A8E] w-10">{unitLabel(i.unit)}</span>
                  </div>
                  <div className="w-24 text-right font-medium" data-testid={`cart-line-total-${i.product_id}`}>
                    CHF {(i.price * Number(i.quantity || 0)).toFixed(2)}
                  </div>
                  <button
                    onClick={() => cart.remove(i.product_id)}
                    className="p-2 text-[#8A9A8E] hover:text-[#D62828]"
                    data-testid={`cart-remove-${i.product_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <aside className="ff-card p-6 h-fit lg:sticky lg:top-28">
              <p className="ff-overline">Récapitulatif</p>
              <div className="mt-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#4A5D4E]">Sous-total</span><span data-testid="cart-subtotal">CHF {cart.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-xs text-[#8A9A8E]"><span>dont TVA 8.1% incluse</span><span>CHF {(cart.subtotal * 0.081 / 1.081).toFixed(2)}</span></div>
              </div>
              <div className="mt-5 pt-5 border-t border-[#E5E3DC] flex justify-between text-lg font-medium">
                <span>Total TTC</span>
                <span className="font-serif text-2xl text-[#2D6A4F]" data-testid="cart-total">CHF {cart.subtotal.toFixed(2)}</span>
              </div>
              <button
                onClick={() => navigate("/checkout")}
                className="ff-btn-primary w-full justify-center mt-6"
                data-testid="cart-checkout-button"
              >
                Valider la commande <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/shop" data-testid="cart-continue-shopping" className="block text-center mt-3 text-sm text-[#4A5D4E] hover:text-[#2D6A4F]">
                Continuer mes achats
              </Link>
            </aside>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
